import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import {
    BABOOR_ENVIRONMENT,
    BaboorEnvironmentConfig,
} from '@baboor/baboor-core'
import { ApiResponse } from '@baboor/common-models'
import { setShopDisplayTimezone } from '@baboor/common-util'
import { Observable, map } from 'rxjs'
import { ShopItem, ShopProfile } from './shops-api.service'

export interface PublicService {
    id: string
    name: string
    description: string | null
    price: string
    duration: number
    category: string | null
    promotion: {
        name: string
        discountType: string
        discountValue: string
        finalPrice: string
    } | null
}

export interface PublicTeamMember {
    id: string
    userId: string
    role: string
    bio: string | null
    firstName: string | null
    lastName: string | null
    profilePhoto: string | null
}

export interface AvailableSlot {
    id: string
    startTime: string
    endTime: string
    displayTime: string
    duration: number
    available: boolean
    reason: 'booked' | 'past' | 'unavailable' | null
    bookingCount: number
    maxCapacity: number
    remainingCapacity: number
    assignedBarber: string | null
}

export interface AvailableSlotsResponse {
    shopId: string
    date: string
    timezone: string
    slotDuration: number
    slotInterval: number
    isOpen: boolean
    openingTime: string | null
    closingTime: string | null
    slots: AvailableSlot[]
}

export interface CreatePublicAppointmentRequest {
    clientName: string
    clientPhone: string
    clientEmail?: string
    scheduledAt: string
    teamMemberId?: string
    serviceId?: string
    duration?: number
    price?: string
    notes?: string
}

export interface AppointmentDetail {
    id: string
    scheduledAt: string
    duration: number
    status: string
    clientName: string
    clientPhone: string
    clientEmail: string | null
    staffName: string | null
    serviceName: string | null
    price: string | null
}

export interface PublicShopDiscoveryItem {
    id: string
    name: string
    email: string | null
    phone: string | null
    address: string | null
    city: string | null
    state: string | null
    country: string | null
    postCode: string | null
    latitude: number | null
    longitude: number | null
    verifiedOn: string | null
    createdAt: string | null
    updatedAt: string | null
    planName?: string | null
    logoUrl?: string | null
    coverUrl?: string | null
    description?: string | null
    timezone?: string | null
    slotInterval?: number | null
    distanceMiles?: number | null
    averageRating?: number | null
    reviewCount?: number
    isOpen?: boolean | null
    todayHours?: {
        day: string
        isOpen: boolean
        openingTime: string | null
        closingTime: string | null
    } | null
    startingPrice?: string | null
    services?: {
        id: string
        name: string
        price: string
        duration: number
        category: string | null
    }[]
}

export interface PublicShopsQueryParams {
    search?: string
    city?: string
    state?: string
    zipCode?: string
    address?: string
    latitude?: number
    longitude?: number
    radiusMiles?: number
    openNow?: boolean
    minRating?: number
    serviceId?: string
    serviceCategory?: string
    sortBy?: 'name' | 'nearest' | 'highest_rated' | 'relevance'
    sortOrder?: 'asc' | 'desc'
    page?: number
    size?: number
}

@Injectable({ providedIn: 'root' })
export class PublicShopsApiService {
    private apiUrl: string

    constructor(
        private http: HttpClient,
        @Inject(BABOOR_ENVIRONMENT) private env: BaboorEnvironmentConfig,
    ) {
        this.apiUrl = `${env.apiUrl}/public/v1/shops`
    }

    getAll(
        params?: PublicShopsQueryParams,
    ): Observable<ApiResponse<PublicShopDiscoveryItem[]>> {
        let httpParams = new HttpParams()
        if (params) {
            Object.entries(params).forEach(([key, val]) => {
                if (val !== undefined && val !== null && val !== '') {
                    httpParams = httpParams.set(key, String(val))
                }
            })
        }
        return this.http.get<ApiResponse<PublicShopDiscoveryItem[]>>(
            this.apiUrl,
            {
                params: httpParams,
            },
        )
    }

    getProfile(id: string): Observable<ApiResponse<ShopProfile>> {
        return this.http
            .get<ApiResponse<ShopProfile>>(`${this.apiUrl}/${id}/profile`)
            .pipe(
                // The profile reports the shop's zone; adopt it so every
                // appointment time renders on the shop's clock.
                map((response) => {
                    setShopDisplayTimezone(response.data?.shop?.timezone)
                    return response
                }),
            )
    }

    getServices(id: string): Observable<ApiResponse<PublicService[]>> {
        return this.http.get<ApiResponse<PublicService[]>>(
            `${this.apiUrl}/${id}/services`,
        )
    }

    getTeam(id: string): Observable<ApiResponse<PublicTeamMember[]>> {
        return this.http.get<ApiResponse<PublicTeamMember[]>>(
            `${this.apiUrl}/${id}/team`,
        )
    }

    getAvailableSlots(
        id: string,
        date: string,
        staffId?: string,
        serviceId?: string,
    ): Observable<ApiResponse<AvailableSlotsResponse>> {
        let params = new HttpParams().set('date', date)
        if (staffId) params = params.set('staffId', staffId)
        if (serviceId) params = params.set('serviceId', serviceId)
        return this.http
            .get<ApiResponse<AvailableSlotsResponse>>(
                `${this.apiUrl}/${id}/available-slots`,
                { params },
            )
            .pipe(
                // The grid is generated on the shop's wall clock; adopt the
                // reported zone so every time renderer reads the same clock.
                map((response) => {
                    setShopDisplayTimezone(response.data?.timezone)
                    return response
                }),
            )
    }

    createAppointment(
        id: string,
        data: CreatePublicAppointmentRequest,
    ): Observable<ApiResponse<AppointmentDetail>> {
        return this.http.post<ApiResponse<AppointmentDetail>>(
            `${this.apiUrl}/${id}/appointments`,
            data,
        )
    }
}
